import { Plane, Car, BedDouble, Camera, MapPin } from "lucide-react";
import { formatPrice } from "@/lib/currency";

/**
 * Phase 9 — Holiday package card with itemised breakdown.
 *
 * Bundles flight (round-trip), ground transit (round-trip), hotel
 * nights, and sightseeing. Surfaced when the user asks for a "package
 * to {non-airport destination}". The category dot is purple to match
 * the existing packages spec.
 */
export default function HolidayPackageCard({ card }) {
    const {
        destination,
        duration_days,
        duration_nights,
        via_airport,
        components = {},
        total_inr,
        no_flight_needed,
        book_url,
        vibe,
    } = card;
    const fmtINR = (amt) => formatPrice({ amount: amt, currency: "INR" });

    const rows = [
        components.flight && {
            icon: Plane,
            label: components.flight.label,
            value: fmtINR(components.flight.price),
            testid: "pkg-row-flight",
        },
        components.ground_transit && {
            icon: Car,
            label: components.ground_transit.label,
            value: fmtINR(components.ground_transit.price),
            testid: "pkg-row-transit",
        },
        components.hotel && {
            icon: BedDouble,
            label: `${components.hotel.label} · ${fmtINR(components.hotel.price_per_night)}/night × ${components.hotel.nights}`,
            value: fmtINR(components.hotel.total),
            testid: "pkg-row-hotel",
        },
        components.sightseeing && {
            icon: Camera,
            label: components.sightseeing.summary,
            value: fmtINR(components.sightseeing.estimated_cost),
            testid: "pkg-row-sightseeing",
        },
    ].filter(Boolean);

    return (
        <article
            className="relative rounded-2xl border border-[#E5E5E5] bg-white p-5"
            data-testid="holiday-package-card"
        >
            <span
                aria-hidden
                className="absolute right-4 top-4 inline-block h-2 w-2 rounded-full"
                style={{ background: "#7C3AED" }}
            />
            <header className="mb-3">
                <h3 className="text-[16px] font-semibold tracking-tight text-[#0A0A0A]">
                    {duration_days}D{duration_nights}N {destination?.name}
                    {vibe ? <span className="text-[#737373]"> · {vibe.replace("_", " ")}</span> : null}
                </h3>
                <p className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-[#737373]">
                    <MapPin size={11} strokeWidth={2} />
                    via {via_airport?.city} ({via_airport?.iata})
                    {no_flight_needed ? <em className="not-italic text-[#A3A3A3]"> · no flight needed</em> : null}
                </p>
            </header>

            <ul className="divide-y divide-[#E5E5E5] border-y border-[#E5E5E5]">
                {rows.map((r) => (
                    <li
                        key={r.testid}
                        className="flex items-center gap-3 py-3 text-[13px] text-[#0A0A0A]"
                        data-testid={r.testid}
                    >
                        <r.icon size={14} strokeWidth={2} className="shrink-0 text-[#737373]" />
                        <span className="flex-1">{r.label}</span>
                        <span className="font-semibold tabular-nums">{r.value}</span>
                    </li>
                ))}
            </ul>

            <div className="mt-4 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-[#737373]">Total</span>
                <span className="text-[20px] font-semibold tracking-tight text-[#0A0A0A]" data-testid="pkg-total">
                    {fmtINR(total_inr)}
                </span>
            </div>

            {book_url && (
                <a
                    href={book_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pkg-book-now"
                    className="mt-4 inline-flex w-full items-center justify-center rounded-lg bg-[#0A0A0A] px-4 py-2 text-[13px] font-medium text-white transition hover:bg-black"
                >
                    Book this package
                </a>
            )}
        </article>
    );
}
